import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "../../utils/axiosConfig";
import "../../styles/features/events/EventDetails.css";

export default function InvitationEventDetails() {
  const navigate = useNavigate();
  const { invitationId } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [msg, setMsg] = useState("");

  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: "https://someet-api" },
    });

    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const loadDetails = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`/me/invitations/${invitationId}/event`, { headers });
      setDetails(data);
      setActiveImg(0);
    } catch {
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, invitationId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const acceptInvite = async () => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(`/me/invitations/${invitationId}/accept`, {}, { headers });
      setMsg("Zaproszenie zaakceptowane. Wyślemy Ci e-mail z informacją o płatności.");
      await loadDetails();
    } catch (err) {
      setMsg(err.response?.data?.message || "Nie udało się zaakceptować zaproszenia.");
    }
  };

  const declineInvite = async () => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(`/me/invitations/${invitationId}/decline`, {}, { headers });
      setMsg("Zaproszenie zostało odrzucone.");
      await loadDetails();
    } catch (err) {
      setMsg(err.response?.data?.message || "Nie udało się odrzucić zaproszenia.");
    }
  };

  const event = details?.event;
  const invitation = details?.invitation;

  const priceLabel = useMemo(() => {
    if (!event) return "";
    return event.price != null ? `${event.price} ${event.currency || "PLN"}` : "";
  }, [event]);

  if (loading) {
    return (
      <div className="eventDetails page">
        <div className="eventDetails__container">
          <div className="eventDetails__skeleton" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="eventDetails page">
        <div className="eventDetails__container">
          <h2>Nie masz dostępu do tego zaproszenia albo wydarzenie nie istnieje.</h2>
          <button className="eventDetails__back" onClick={() => navigate("/profile")}>
            Wróć do profilu
          </button>
        </div>
      </div>
    );
  }

  const isPending = invitation?.status === "PENDING";

  return (
    <div className="eventDetails page">
      <div className="eventDetails__container">
        <div className="eventDetails__top">
          <button className="eventDetails__back" onClick={() => navigate("/profile")}>
            ← Mój profil
          </button>

          <div className="eventDetails__typeBadge">
            Wydarzenie dla singli
          </div>
        </div>

        {msg && <div className="alert">{msg}</div>}

        <div className="eventDetails__grid">
          <div className="eventDetails__left">
            <div className="eventDetails__gallery">
              <div className="eventDetails__mainImgWrap">
                <img
                  className="eventDetails__mainImg"
                  src={event.images?.[activeImg] || "/images/single.jpg"}
                  alt={event.title}
                />
              </div>

              <div className="eventDetails__thumbs">
                {(event.images || []).map((src, idx) => (
                  <button
                    key={`${src}-${idx}`}
                    className={`eventDetails__thumbBtn ${idx === activeImg ? "isActive" : ""}`}
                    onClick={() => setActiveImg(idx)}
                    type="button"
                  >
                    <img className="eventDetails__thumbImg" src={src} alt="" />
                  </button>
                ))}
              </div>
            </div>

            <div className="eventDetails__header">
              <h1 className="eventDetails__title">{event.title}</h1>
              <p className="eventDetails__subtitle">{event.subtitle}</p>

              <div className="eventDetails__metaRow">
                <span className="pill">📍 {event.city} · {event.place}</span>
                <span className="pill">🗓️ {event.date} · {event.time}</span>
                <span className="pill">⏳ {event.durationMin} min</span>
                {event.ageLimit ? <span className="pill">🔞 {event.ageLimit}+</span> : null}
              </div>
            </div>

            <section className="eventDetails__section">
              <h3>Opis wydarzenia</h3>
              <p className="eventDetails__text">{event.description}</p>
            </section>

            {!!event.included?.length && (
              <section className="eventDetails__section">
                <h3>Co jest w cenie</h3>
                <ul className="eventDetails__list">
                  {event.included.map((x) => <li key={x}>{x}</li>)}
                </ul>
              </section>
            )}

            {!!event.agenda?.length && (
              <section className="eventDetails__section">
                <h3>Plan</h3>
                <ul className="eventDetails__list">
                  {event.agenda.map((x) => <li key={x}>{x}</li>)}
                </ul>
              </section>
            )}
          </div>

          <aside className="eventDetails__right">
            <div className="eventDetails__card">
              <div className="eventDetails__priceRow">
                <div className="eventDetails__price">{priceLabel}</div>
                <div className="eventDetails__status ok">
                  {invitation?.status === "PENDING"
                    ? "Oczekuje"
                    : invitation?.status === "ACCEPTED"
                      ? "Zaakceptowane"
                      : invitation?.status === "DECLINED"
                        ? "Odrzucone"
                        : "Wygasłe"}
                </div>
              </div>

              <div className="eventDetails__info">
                <div className="eventDetails__infoRow">
                  <span className="muted">Miejsce</span>
                  <span>{event.address}</span>
                </div>

                {invitation?.status === "PENDING" && (
                <div className="eventDetails__infoRow">
                    <span className="muted">Odpowiedz do</span>
                    <span>{new Date(invitation.responseDeadline).toLocaleString("pl-PL")}</span>
                </div>
                )}

                {invitation?.status === "ACCEPTED" && invitation?.paymentDeadline && (
                <div className="eventDetails__infoRow">
                    <span className="muted">Termin płatności</span>
                    <span>{new Date(invitation.paymentDeadline).toLocaleString("pl-PL")}</span>
                </div>
                )}
              </div>

              {isPending && (
                <div className="invite-details-actions">
                  <button className="btn-invite btn-invite-accept" onClick={acceptInvite}>
                    Akceptuję zaproszenie
                  </button>

                  <button className="btn-invite btn-invite-decline" onClick={declineInvite}>
                    Odrzucam
                  </button>
                </div>
              )}

                <p className="eventDetails__note">
                {invitation?.status === "ACCEPTED"
                    ? "Zaproszenie zostało zaakceptowane. Wyślemy Ci e-mail z informacją o płatności."
                    : "Po zaakceptowaniu zaproszenia wyślemy Ci e-mail z informacją o płatności. Rezerwacja miejsca będzie ważna przez określony czas."}
                </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}